import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios/axiosConfig';
import { formatDateUnified } from '../../utils/dateUtils';
import { portalApi } from '../api/portalApi';
import { fetchData } from './apiCallSlice';
import { selectApiUrls } from './apiUrlSlice';

const BOOKING_LOG_YEAR = 2026;

const normalizeIdValue = (value) => String(value ?? '').trim();
const getRecordId = (record) => record?.ProjectID ?? record?.ID ?? record?.projectID ?? record?.id ?? null;
const idsMatch = (left, right) => {
  const normalizedLeft = normalizeIdValue(left);
  const normalizedRight = normalizeIdValue(right);
  if (!normalizedLeft || !normalizedRight) return false;
  return normalizedLeft === normalizedRight;
};

// Helper function to safely extract month and year from date strings
// This avoids timezone issues that can cause dates to shift to previous month/year
const getDateMonthYear = (dateString) => {
  if (!dateString) return { month: null, year: null };
  
  try {
    // Handle SQL Server datetime format: "2025-02-20 00:00:00.0000000"
    if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes(':')) {
      const datePart = dateString.split(' ')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month] = datePart.split('-');
        return {
          month: parseInt(month, 10),
          year: parseInt(year, 10)
        };
      }
    }
    
    // Handle ISO date format: "YYYY-MM-DD" - treat as local to avoid timezone issues
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month] = dateString.split('-');
      return {
        month: parseInt(month, 10),
        year: parseInt(year, 10)
      };
    }
    
    // Handle ISO datetime format with 'T' (e.g., "2025-02-20T00:00:00.000")
    if (typeof dateString === 'string' && dateString.includes('T')) {
      const datePart = dateString.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month] = datePart.split('-');
        return {
          month: parseInt(month, 10),
          year: parseInt(year, 10)
        };
      }
    }
    
    // For other formats, use the unified formatter to get a consistent date
    // then parse the components locally to avoid timezone issues
    const isoDate = formatDateUnified(dateString, 'iso');
    if (isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      const [year, month] = isoDate.split('-');
      return {
        month: parseInt(month, 10),
        year: parseInt(year, 10)
      };
    }
    
    // Fallback: try standard Date parsing but log warning
    console.warn(`getDateMonthYear: Falling back to Date parsing for: ${dateString}`);
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return {
        month: date.getMonth() + 1, // 1-12
        year: date.getFullYear()
      };
    }
    
  } catch (error) {
    console.error('Error parsing date in getDateMonthYear:', error, 'Input:', dateString);
  }
  
  return { month: null, year: null };
};

// Helper function to safely create a Date object for sorting without timezone issues
const createSafeDate = (dateString) => {
  if (!dateString) return new Date(0); // Return epoch for null dates to sort them first
  
  try {
    // Extract date components using the same logic as getDateMonthYear
    const { month, year } = getDateMonthYear(dateString);
    if (month && year) {
      // Extract day component similarly
      let day = 1; // default
      
      if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes(':')) {
        const datePart = dateString.split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          day = parseInt(datePart.split('-')[2], 10);
        }
      } else if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        day = parseInt(dateString.split('-')[2], 10);
      } else if (typeof dateString === 'string' && dateString.includes('T')) {
        const datePart = dateString.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          day = parseInt(datePart.split('-')[2], 10);
        }
      }
      
      // Create date with local components - no timezone issues
      return new Date(year, month - 1, day); // month is 0-based in Date constructor
    }
    
    // Fallback to standard parsing
    return new Date(dateString);
  } catch (error) {
    console.error('Error creating safe date:', error, 'Input:', dateString);
    return new Date(0);
  }
};

// Helper function to normalize state names to standard two-letter abbreviations
const normalizeStateName = (stateName) => {
  if (!stateName) return null;
  
  // Remove any trailing punctuation and trim whitespace
  const cleanState = stateName.toString().trim().replace(/[.,;:!?]$/, '');
  
  // If it's already a 2-letter abbreviation, return uppercase
  if (cleanState.length === 2) {
    return cleanState.toUpperCase();
  }
  
  // State name to abbreviation mapping
  const stateMap = {
    // Full state names
    'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
    'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
    'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
    'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
    'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
    'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
    'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY',
    
    // Common variations and territories
    'DISTRICT OF COLUMBIA': 'DC', 'WASHINGTON DC': 'DC', 'WASHINGTON D.C.': 'DC',
    'PUERTO RICO': 'PR', 'VIRGIN ISLANDS': 'VI', 'GUAM': 'GU', 'AMERICAN SAMOA': 'AS',
    'NORTHERN MARIANA ISLANDS': 'MP',
    
    // Common abbreviations that might have periods
    'WASH': 'WA', 'CALIF': 'CA', 'FLA': 'FL', 'MASS': 'MA', 'PENN': 'PA', 'TENN': 'TN'
  };
  
  const upperState = cleanState.toUpperCase();
  return stateMap[upperState] || cleanState.toUpperCase();
};

// Async thunk to fetch all booking data from multiple tables
export const fetchBookingsData = createAsyncThunk(
  'bookingsLog/fetchBookingsData',
  async (filters = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const apiUrls = selectApiUrls(getState());
      const requestedYear = Number(filters?.year) || BOOKING_LOG_YEAR;
      const bookingLogUrl = `${apiUrls.bookingLogFull}?year=${requestedYear}`;

      // Fetch the consolidated booking payload and keep remaining tables as-is.
      const bookingLogFullResult = await (async () => {
        try {
          return await dispatch(fetchData({ url: bookingLogUrl })).unwrap();
        } catch (initialError) {
          // Some environments intermittently return a transient 500 on the first
          // RTK Query request; fall back to direct axios and let interceptor retries run.
          const fallbackResponse = await axiosInstance.get(bookingLogUrl, {
            headers: {
              'x-client-source': 'bookingsLog:axios-fallback',
            },
          });
          return fallbackResponse?.data;
        }
      })();

      const salesTerritoryResult = await (
        dispatch(portalApi.endpoints.getSalesTerritory.initiate(undefined, { forceRefetch: false }))
      );

      const bookingLogRows = Array.isArray(bookingLogFullResult)
        ? bookingLogFullResult
        : Array.isArray(bookingLogFullResult?.data)
          ? bookingLogFullResult.data
          : [];

      const soldToShipTo = [];
      const priceBreakdown = [];
      const bookingJobDetail = [];
      const commissionSplits = [];

      bookingLogRows.forEach((row) => {
        const parentProjectId = row?.ID ?? row?.ProjectID ?? null;

        const soldToRows = Array.isArray(row?.SoldToShipTo) ? row.SoldToShipTo : [];
        soldToRows.forEach((item) => {
          soldToShipTo.push({
            ...item,
            ProjectID: item?.ProjectID ?? parentProjectId,
          });
        });

        const priceRows = Array.isArray(row?.PriceBreakdown) ? row.PriceBreakdown : [];
        priceRows.forEach((item) => {
          priceBreakdown.push({
            ...item,
            ProjectID: item?.ProjectID ?? parentProjectId,
          });
        });

        const bookingRows = Array.isArray(row?.BookingJobDetail) ? row.BookingJobDetail : [];
        bookingRows.forEach((item) => {
          bookingJobDetail.push({
            ...item,
            ProjectID: item?.ProjectID ?? parentProjectId,
          });
        });

        const commissionRows = Array.isArray(row?.BookingCommissionSplit) ? row.BookingCommissionSplit : [];
        commissionRows.forEach((item) => {
          commissionSplits.push({
            ...item,
            ProjectID: item?.ProjectID ?? parentProjectId,
          });
        });
      });

      return {
        generalProject: bookingLogRows,
        soldToShipTo,
        priceBreakdown,
        bookingJobDetail,
        commissionSplits,
        salesTerritory: salesTerritoryResult.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Helper function to convert commission split data for API - handles POST (camelCase) vs PATCH (PascalCase)
function convertToApiFormat(data, operationType = 'POST') {
  const fieldMap = {
    ID: 'id',
    ProjectID: 'projectID', 
    SaleQuoteID: 'saleQuoteId',
    BookingJobDetailID: 'bookingJobDetailID',
    SalesRep: 'salesRep',
    CommissionPercentage: 'commissionPercentage',
    CommissionAmount: 'commissionAmount'
  };
  
  const converted = {};
  
  // Convert based on operation type
  if (operationType === 'POST') {
    // POST requires camelCase (first letter lowercase)
    for (const [key, value] of Object.entries(data)) {
      const camelCaseKey = fieldMap[key] || (key.charAt(0).toLowerCase() + key.slice(1));
      converted[camelCaseKey] = value;
    }
  } else if (operationType === 'PATCH') {
    // PATCH requires PascalCase (first letter uppercase)
    for (const [key, value] of Object.entries(data)) {
      const pascalCaseKey = key.charAt(0).toUpperCase() + key.slice(1);
      converted[pascalCaseKey] = value;
    }
  }
  
  return converted;
}

// Helper function to convert data to camelCase for POST operations
function convertPostDataToCamelCase(data) {
  const converted = {};
  for (const [key, value] of Object.entries(data)) {
    // Convert PascalCase to camelCase (first letter lowercase)
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    converted[camelKey] = value;
  }
  return converted;
}

// Helper function to generate default commission split values based on remaining balance
export function generateDefaultCommissionSplit(projectId, existingSplits, invoicePrice) {
  const totalPercentage = existingSplits.reduce((sum, split) => {
    const percentage = parseFloat(split.CommissionPercentage) || 0;
    return sum + percentage;
  }, 0);
  
  const totalAmount = existingSplits.reduce((sum, split) => {
    const amount = parseFloat(split.CommissionAmount) || 0;
    return sum + amount;
  }, 0);
  
  const remainingPercentage = Math.max(0, 100 - totalPercentage);
  const remainingAmount = Math.max(0, parseFloat(invoicePrice) - totalAmount);
  
  const result = {
    ProjectID: projectId,
    SalesRep: '',
    CommissionPercentage: remainingPercentage,
    CommissionAmount: remainingAmount
  };
  
  return result;
}

// Async thunk to create a new commission split
export const createCommissionSplit = createAsyncThunk(
  'bookingsLog/createCommissionSplit',
  async (splitData, { dispatch, rejectWithValue }) => {
    try {
      // Check if BookingJobDetailID exists, if not create a new bookingJobDetail record first
      if (!splitData.BookingJobDetailID) {
        const createBookingResult = await dispatch(updateBookingRecord({
          table: 'bookingJobDetail',
          id: splitData.ProjectID,
          field: 'ProjectID',
          value: splitData.ProjectID,
          projectId: splitData.ProjectID
        })).unwrap();

        const newBookingJobDetailID = createBookingResult.response?.ID;
        if (newBookingJobDetailID) {
          splitData.BookingJobDetailID = newBookingJobDetailID;
        } else {
          throw new Error('Failed to create bookingJobDetail record or get ID');
        }
      }

      // Convert to camelCase format for POST API
      const apiData = convertToApiFormat(splitData, 'POST');

      const result = await dispatch(
        portalApi.endpoints.createCommissionSplitRecord.initiate(apiData)
      );

      if (result.error) {
        throw new Error(result.error.data || 'Failed to create commission split');
      }

      const responseData = result.data;
      const normalizedData = {
        ID: responseData.ID || responseData.id,
        ProjectID: responseData.ProjectID || responseData.projectID || splitData.ProjectID,
        SaleQuoteID: responseData.SaleQuoteID || responseData.saleQuoteId,
        BookingJobDetailID: responseData.BookingJobDetailID || responseData.bookingJobDetailID || splitData.BookingJobDetailID,
        SalesRep: responseData.SalesRep || responseData.salesRep,
        CommissionPercentage: responseData.CommissionPercentage || responseData.commissionPercentage,
        CommissionAmount: responseData.CommissionAmount || responseData.commissionAmount
      };

      return normalizedData;
    } catch (error) {
      console.error('createCommissionSplit error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk to update a commission split
export const updateCommissionSplit = createAsyncThunk(
  'bookingsLog/updateCommissionSplit',
  async ({ id, ...splitData }, { getState, dispatch, rejectWithValue }) => {
    try {
      const apiData = convertToApiFormat(splitData, 'PATCH');

      const result = await dispatch(
        portalApi.endpoints.updateCommissionSplitRecord.initiate({ id, data: apiData })
      );

      if (result.error) {
        throw new Error(result.error.data || 'Failed to update commission split');
      }

      let normalizedData;
      const responseData = result.data;

      if (typeof responseData === 'string' && responseData.includes('updated successfully')) {
        const existingCommissionSplits = getState().bookingsLog.data?.commissionSplits || [];
        const existingSplit = existingCommissionSplits.find(split => split.ID === id);

        if (existingSplit) {
          normalizedData = { ...existingSplit, ...splitData, ID: id };
        } else {
          console.error('updateCommissionSplit - Could not find existing commission split in state for ID:', id);
          throw new Error('Could not reconstruct commission split - original data not found');
        }
      } else {
        normalizedData = {
          ID: responseData.ID || responseData.id || id,
          ProjectID: responseData.ProjectID || responseData.projectID,
          SaleQuoteID: responseData.SaleQuoteID || responseData.saleQuoteId,
          BookingJobDetailID: responseData.BookingJobDetailID || responseData.bookingJobDetailID,
          SalesRep: responseData.SalesRep || responseData.salesRep,
          CommissionPercentage: responseData.CommissionPercentage || responseData.commissionPercentage,
          CommissionAmount: responseData.CommissionAmount || responseData.commissionAmount
        };
      }

      return normalizedData;
    } catch (error) {
      console.error('updateCommissionSplit error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk to delete a commission split
export const deleteCommissionSplit = createAsyncThunk(
  'bookingsLog/deleteCommissionSplit',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        portalApi.endpoints.deleteCommissionSplitRecord.initiate(id)
      );

      if (result.error) {
        throw new Error(result.error.data || 'Failed to delete commission split');
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Helper function to identify date fields and convert empty strings to null
function processDateFieldValue(field, table, value) {
  // Define date fields for each table
  const dateFields = {
    generalProject: [
      'OrderDate',
      'RequestedDeliveryDate'
    ],
    soldToShipTo: [
      // No date fields currently identified
    ],
    priceBreakdown: [
      // No date fields currently identified
    ],
    bookingJobDetail: [
      'RequestedDeliveryDate',
      'CSOSubmittalSent',
      'CSOSubmittalReceived', 
      'SignedOrderReceived',
      'SalesmanOrderSheetReceived',
      'EngineeringHold',
      'LongLeads',
      'FullRelease'
    ]
  };
  
  // Map UI field name to API field name first
  const apiFieldName = mapFieldNameToApi(field, table);
  
  // Check if this is a date field
  const isDateField = dateFields[table]?.includes(apiFieldName) || false;
  
  // Convert empty string to null for date fields
  if (isDateField && value === '') {
    return null;
  }
  
  return value;
}

// Helper function to map UI field names to API field names
function mapFieldNameToApi(field, table) {
  const fieldMappings = {
    generalProject: {
      RepSalesPerson: 'SalesRep',
      Apps: 'ProjectManager',
      Model: 'ModelNumber', // Map UI field "Model" to API field "ModelNumber"
      // Other fields use the same name
    },
    soldToShipTo: {
      Customer: 'SoldToCompanyName',
      // Other fields use the same name
    },
    priceBreakdown: {
      InvoicePrice: 'SellPriceAmount',
      // Other fields use the same name
    },
    bookingJobDetail: {
      SalesRep: 'Rep',
      RepSalesPerson: 'SalesRep',
      MappedSalesPerson: 'MappedSalesRep',
      CustomerReqType: 'RequestedDeliveryType',
      DeliveryMonth: 'RequestedDeliveryDate',
      Multiplier: 'SalesMultiplier',
      CSOSubmittalSent: 'CSOSubmittalSent',
      CSOSubmittalReceived: 'CSOSubmittalReceived',
      SignedOrderReceived: 'SignedOrderReceived',
      SalesmanOrderSheetReceived: 'OrderSheetReceived',
      EngineeringHold: 'EngineeringReleaseOnly',
      LongLeads: 'LongLeadReleaseOnly',
      FullRelease: 'FullRelease',
      AuthorizedBy: 'AuthorizedBy',
      // Other fields use the same name
    }
  };
  
  return fieldMappings[table]?.[field] || field;
}

// Async thunk to update a single booking record
export const updateBookingRecord = createAsyncThunk(
  'bookingsLog/updateBookingRecord',
  async ({ table, id, field, value, projectId, ProjectID }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const normalizedProjectID = projectId ?? ProjectID;

      // Map UI field name to API field name
      const apiFieldName = mapFieldNameToApi(field, table);

      // Process date fields to convert empty strings to null
      const processedValue = processDateFieldValue(field, table, value);

      // Get the actual record to find its ID
      const tableData = state.bookingsLog.data[table];
      const existingRecord = tableData.find((record) =>
        idsMatch(getRecordId(record), normalizedProjectID)
      );

      let result;

      switch (table) {
        case 'generalProject': {
          if (!existingRecord) throw new Error('Record not found for generalProject update');
          const recordId = existingRecord.ID || existingRecord.ProjectID;
          result = await dispatch(
            portalApi.endpoints.updateGeneralProjectRecord.initiate({
              id: recordId,
              data: {
                ID: recordId,
                [apiFieldName]: processedValue,
              },
            })
          );
          break;
        }

        case 'soldToShipTo': {
          if (!existingRecord) throw new Error('Record not found for soldToShipTo update');
          const recordId = existingRecord.ID || existingRecord.ProjectID;
          result = await dispatch(
            portalApi.endpoints.updateSoldToShipToRecord.initiate({
              id: recordId,
              data: {
                ID: recordId,
                [apiFieldName]: processedValue,
              },
            })
          );
          break;
        }

        case 'priceBreakdown': {
          if (!existingRecord) throw new Error('Record not found for priceBreakdown update');
          const recordId = existingRecord.ID || existingRecord.ProjectID;
          result = await dispatch(
            portalApi.endpoints.updatePriceBreakdownRecord.initiate({
              id: recordId,
              data: {
                ID: recordId,
                [apiFieldName]: processedValue,
              },
            })
          );
          break;
        }

        case 'bookingJobDetail': {
          if (existingRecord) {
            const recordId = existingRecord.ID || existingRecord.ProjectID;
            result = await dispatch(
              portalApi.endpoints.updateBookingJobDetailRecord.initiate({
                id: recordId,
                data: {
                  ID: recordId,
                  [apiFieldName]: processedValue,
                },
              })
            );
          } else {
            // POST new record – camelCase field names
            const bookingCreateData = convertPostDataToCamelCase({
              [apiFieldName]: processedValue,
              ProjectID: normalizedProjectID,
            });
            result = await dispatch(
              portalApi.endpoints.createBookingJobDetailRecord.initiate(bookingCreateData)
            );
          }
          break;
        }

        default:
          throw new Error(`Unknown table: ${table}`);
      }

      if (result?.error) {
        throw new Error(result.error.data || `Failed to update ${table}`);
      }

      return {
        table,
        id,
        field,
        value,
        projectId: normalizedProjectID,
        response: result?.data,
        isNewRecord: table === 'bookingJobDetail' && !existingRecord,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const bookingsLogSlice = createSlice({
  name: 'bookingsLog',
  initialState: {
    data: {
      generalProject: [],
      soldToShipTo: [],
      priceBreakdown: [],
      bookingJobDetail: [],
      commissionSplits: [],
      salesTerritory: []
    },
    processedData: [], // Combined and processed data for display
    loading: false,
    error: null,
    selectedMonth: new Date().getMonth() + 1, // Current month (1-12), 0 = All months
    selectedYear: new Date().getFullYear()
  },
  reducers: {
    replaceRealtimeBookings: (state, action) => {
      const rows = action.payload;
      state.data.generalProject = rows;
      for (const [source, target] of Object.entries({ SoldToShipTo: 'soldToShipTo', PriceBreakdown: 'priceBreakdown', BookingJobDetail: 'bookingJobDetail', BookingCommissionSplit: 'commissionSplits' })) {
        state.data[target] = rows.flatMap(row => (Array.isArray(row[source]) ? row[source] : []).map(item => ({ ...item, ProjectID: item.ProjectID ?? row.ID ?? row.ProjectID })));
      }
      state.processedData = combineAndProcessAllData(state.data);
    },
    replaceRealtimeTerritories: (state, action) => {
      state.data.salesTerritory = action.payload;
      state.processedData = combineAndProcessAllData(state.data);
    },
    clear: (state) => {
      state.data = {
        generalProject: [],
        soldToShipTo: [],
        priceBreakdown: [],
        bookingJobDetail: [],
        commissionSplits: [],
        salesTerritory: []
      };
      state.processedData = [];
      state.loading = false;
      state.error = null;
    },
    setSelectedMonth: (state, action) => {
      state.selectedMonth = action.payload;
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
    updateLocalRecord: (state, action) => {
      const { table, projectId, field, value } = action.payload;
      
      // Map UI field name to the actual data field name
      const getDataFieldName = (field, table) => {
        const dataFieldMappings = {
          generalProject: {
            RepSalesPerson: 'SalesRep',
            Apps: 'ProjectManager',
            Model: 'ModelNumber' // Map UI field "Model" to data field "ModelNumber"
          },
          soldToShipTo: {
            Customer: 'SoldToCompanyName'
          },
          priceBreakdown: {
            InvoicePrice: 'SellPriceAmount'
          },
          bookingJobDetail: {
            MappedSalesPerson: 'MappedSalesRep',
            CustomerReqType: 'RequestedDeliveryType',
            DeliveryMonth: 'RequestedDeliveryDate',
            Multiplier: 'SalesMultiplier',
            SalesmanOrderSheetReceived: 'OrderSheetReceived',
            EngineeringHold: 'EngineeringReleaseOnly',
            LongLeads: 'LongLeadReleaseOnly'
          }
        };
        
        return dataFieldMappings[table]?.[field] || field;
      };
      
      const dataFieldName = getDataFieldName(field, table);
      
      // Update the local data - handle both ID and ProjectID fields
      const tableData = state.data[table];
      const recordIndex = tableData.findIndex((record) =>
        idsMatch(getRecordId(record), projectId)
      );
      
      if (recordIndex !== -1) {
        tableData[recordIndex][dataFieldName] = value;
        
        // Re-process all data to update display immediately
        state.processedData = combineAndProcessAllData(state.data);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookingsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingsData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        
        // Process and combine ALL data for storage (don't filter by month/year here)
        state.processedData = combineAndProcessAllData(action.payload);
      })
      .addCase(fetchBookingsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBookingRecord.pending, (state) => {
        // Clear any previous errors
        state.error = null;
      })
      .addCase(updateBookingRecord.fulfilled, (state, action) => {
        const { table, projectId, field, value, isNewRecord, response } = action.payload;
        
        // Map UI field name to the actual data field name used in the data structure
        const getDataFieldName = (field, table) => {
          // These are the field names as they appear in the raw data
          const dataFieldMappings = {
            generalProject: {
              RepSalesPerson: 'SalesRep',
              Apps: 'ProjectManager',
              Model: 'ModelNumber' // Map UI field "Model" to data field "ModelNumber"
            },
            soldToShipTo: {
              Customer: 'SoldToCompanyName'
            },
            priceBreakdown: {
              InvoicePrice: 'SellPriceAmount'
            },
            bookingJobDetail: {
              SalesRep: 'Rep', // Map UI SalesRep field to data field Rep
              MappedSalesPerson: 'MappedSalesRep',
              CustomerReqType: 'RequestedDeliveryType',
              DeliveryMonth: 'RequestedDeliveryDate',
              Multiplier: 'SalesMultiplier',
              SalesmanOrderSheetReceived: 'OrderSheetReceived',
              EngineeringHold: 'EngineeringReleaseOnly',
              LongLeads: 'LongLeadReleaseOnly'
            }
          };
          return dataFieldMappings[table]?.[field] || field;
        };
        
        const dataFieldName = getDataFieldName(field, table);
        
        // Handle new record creation (specifically for bookingJobDetail)
        if (isNewRecord && table === 'bookingJobDetail') {
          // Add new record to the table data
          const newRecord = {
            ProjectID: projectId,
            [dataFieldName]: value,
            ...response // Include any additional fields returned by the API
          };
          state.data[table].push(newRecord);
        } else {
          // Update existing record - handle both ID and ProjectID fields
          const tableData = state.data[table];
          const recordIndex = tableData.findIndex((record) =>
            idsMatch(getRecordId(record), projectId)
          );
          
          if (recordIndex !== -1) {
            // Update the record with the correct data field name
            tableData[recordIndex][dataFieldName] = value;
          }
        }
        
        // Re-process all data to update display immediately
        state.processedData = combineAndProcessAllData(state.data);
      })
      .addCase(updateBookingRecord.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Commission splits reducers
      .addCase(createCommissionSplit.fulfilled, (state, action) => {
        state.data.commissionSplits.push(action.payload);
        
        // Re-process all data to update display immediately
        state.processedData = combineAndProcessAllData(state.data);
      })
      .addCase(createCommissionSplit.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCommissionSplit.fulfilled, (state, action) => {
        // Try to find by ID (handle both casing possibilities)
        const payloadId = action.payload.ID ?? action.payload.id;
        let index = state.data.commissionSplits.findIndex((split) =>
          idsMatch(split?.ID ?? split?.id, payloadId)
        );
        
        if (index !== -1) {
          state.data.commissionSplits[index] = action.payload;
        } else {
          console.error('updateCommissionSplit.fulfilled - Could not find commission split to update!');
          console.error('Looking for ID:', payloadId);
          console.error('Available IDs:', state.data.commissionSplits.map((split) => split.ID ?? split.id));
        }
        
        // Re-process all data to update display immediately (same as POST operations)
        state.processedData = combineAndProcessAllData(state.data);
      })
      .addCase(updateCommissionSplit.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCommissionSplit.fulfilled, (state, action) => {
        state.data.commissionSplits = state.data.commissionSplits.filter(
          split => !idsMatch(split?.ID ?? split?.id, action.payload)
        );
      })
      .addCase(deleteCommissionSplit.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

// Helper function to combine and process ALL data (no month/year filtering)
function combineAndProcessAllData(data) {
  const { generalProject, soldToShipTo, priceBreakdown, bookingJobDetail, commissionSplits, salesTerritory } = data;
  
  // Create a map for quick lookups - handle both ID and ProjectID fields
  const soldToShipToMap = new Map();
  soldToShipTo.forEach(item => {
    const key = getRecordId(item);
    if (key) soldToShipToMap.set(key, item);
  });
  
  const priceBreakdownMap = new Map();
  priceBreakdown.forEach(item => {
    const key = getRecordId(item);
    if (key) priceBreakdownMap.set(key, item);
  });
  
  const bookingJobDetailMap = new Map();
  bookingJobDetail.forEach(item => {
    const key = getRecordId(item);
    if (key) bookingJobDetailMap.set(key, item);
  });

  // Create sales territory lookup map using composite key (State + Market)
  const salesTerritoryMap = new Map();
  salesTerritory.forEach(item => {
    if (item.State && item.Market) {
      const key = `${item.State}|${item.Market}`;
      salesTerritoryMap.set(key, item);
    }
  });

  // Create commission splits map for remaining balance calculations
  const commissionSplitsMap = new Map();
  
  commissionSplits.forEach(split => {
    const projectId = normalizeIdValue(getRecordId(split));
    if (!projectId) return;
    if (!commissionSplitsMap.has(projectId)) {
      commissionSplitsMap.set(projectId, []);
    }
    commissionSplitsMap.get(projectId).push(split);
  });
  
  // Combine all data (no filtering here)
  const combinedData = generalProject
    .filter(project => {
      // Get the project ID - could be ID or ProjectID
      const projectId = getRecordId(project);
      
      // Only filter out ScheduleOnly = Yes
      const soldTo = soldToShipToMap.get(projectId);
      if (soldTo && soldTo.ScheduleOnly === 'Yes') {
        return false;
      }
      
      // Must have an OrderDate to be included
      return project.OrderDate != null;
    })
    .map(project => {
      // Get the project ID - could be ID or ProjectID
      const projectId = getRecordId(project);
      const projectIdStr = normalizeIdValue(projectId);
      
      const soldTo = soldToShipToMap.get(projectId) || {};
      const pricing = priceBreakdownMap.get(projectId) || {};
      const booking = bookingJobDetailMap.get(projectId) || {};
      
      // Calculate remaining commission balance for this project
      const existingSplits = commissionSplitsMap.get(projectIdStr) || [];
      const invoicePrice = parseFloat(pricing.SellPriceAmount) || 0;
      const totalPercentage = existingSplits.reduce((sum, split) => sum + (parseFloat(split.CommissionPercentage) || 0), 0);
      const totalAmount = existingSplits.reduce((sum, split) => sum + (parseFloat(split.CommissionAmount) || 0), 0);
      const remainingPercentage = Math.max(0, 100 - totalPercentage);
      const remainingAmount = Math.max(0, invoicePrice - totalAmount);

      // Lookup MappedSalesRep from salesTerritory based on State and Market
      const rawState = project.State;
      const market = pricing.Market;
      let mappedSalesRep = booking.MappedSalesRep; // Default to existing value
      
      if (rawState && market) {
        // Normalize the state name to standard abbreviation
        const normalizedState = normalizeStateName(rawState);
        
        if (normalizedState) {
          const territoryKey = `${normalizedState}|${market}`;
          const territory = salesTerritoryMap.get(territoryKey);
          
          if (territory && territory.SalesRep) {
            mappedSalesRep = territory.SalesRep;
          }
        }
      }
      
      return {
        // Primary keys - use consistent ProjectID
        ProjectID: projectId,
        JobNumber: project.JobNumber,
        BookingJobDetailID: booking.ID || null, // Add the booking detail ID, null if no record exists
        BookingLogOnly: project.BookingLogOnly, // Add BookingLogOnly from GeneralProject table
        
        // All required fields
        OrderDate: project.OrderDate,
        State: project.State,
        ProjectName: project.ProjectName,
        ProjectStatus: project.ProjectStatus,
        Market: pricing.Market,
        Product: booking.Product,
        Customer: soldTo.SoldToCompanyName,
        Model: project.ModelNumber,
        SalesRep: booking.Rep, // New Sales Rep column (from booking detail)
        RepSalesPerson: project.SalesRep, // Sales Person of Record (from project details)
        MappedSalesPerson: mappedSalesRep, // Lookup from salesTerritory based on State and Market
        Apps: project.ProjectManager,
        InvoicePrice: pricing.SellPriceAmount,
        CustomerReqType: booking.RequestedDeliveryType,
  DeliveryMonth: project.RequestedDeliveryDate, // Use RequestedDeliveryDate from generalProject table
  GrossMarginPercent: soldTo.GrossMarginPercent,
        CSOSubmittalSent: booking.CSOSubmittalSent,
        CSOSubmittalReceived: booking.CSOSubmittalReceived,
        SignedOrderReceived: booking.SignedOrderReceived,
        SalesmanOrderSheetReceived: booking.OrderSheetReceived,
        EngineeringHold: booking.EngineeringReleaseOnly,
        LongLeads: booking.LongLeadReleaseOnly,
        FullRelease: booking.FullRelease,
        AuthorizedBy: booking.AuthorizedBy,
        
        // Commission data for easy access
        CommissionSplits: existingSplits,
        RemainingCommissionPercentage: remainingPercentage,
        RemainingCommissionAmount: remainingAmount,
        
        // For editing purposes, store which table each field belongs to
        _fieldMappings: {
          JobNumber: 'generalProject',
          BookingLogOnly: 'generalProject',
          OrderDate: 'generalProject',
          ProjectName: 'generalProject',
          ProjectStatus: 'generalProject',
          Model: 'generalProject', // Map the display field "Model" to generalProject
          ModelNumber: 'generalProject', // Keep both for compatibility
          SalesRep: 'bookingJobDetail', // New Sales Rep field mapping (Rep field from booking)
          RepSalesPerson: 'generalProject',
          Apps: 'generalProject',
          State: 'generalProject',
          Customer: 'soldToShipTo',
          Market: 'priceBreakdown',
          InvoicePrice: 'priceBreakdown',
          Product: 'bookingJobDetail',
          MappedSalesPerson: 'bookingJobDetail',
          CustomerReqType: 'bookingJobDetail',
          DeliveryMonth: 'bookingJobDetail',
          GrossMarginPercent: 'soldToShipTo',
          CSOSubmittalSent: 'bookingJobDetail',
          CSOSubmittalReceived: 'bookingJobDetail',
          SignedOrderReceived: 'bookingJobDetail',
          SalesmanOrderSheetReceived: 'bookingJobDetail',
          EngineeringHold: 'bookingJobDetail',
          LongLeads: 'bookingJobDetail',
          FullRelease: 'bookingJobDetail',
          AuthorizedBy: 'bookingJobDetail'
        }
      };
    })
    .sort((a, b) => {
      // Sort by JobNumber - move blank job numbers to the bottom
      const aJobNumber = a.JobNumber || '';
      const bJobNumber = b.JobNumber || '';
      
      // If both are empty, maintain original order
      if (!aJobNumber && !bJobNumber) return 0;
      
      // If only a is empty, put it after b
      if (!aJobNumber) return 1;
      
      // If only b is empty, put a before b  
      if (!bJobNumber) return -1;
      
      // Both have values, sort normally
      return aJobNumber.localeCompare(bJobNumber, undefined, { numeric: true });
    });
  
  return combinedData;
}

export const { clear, setSelectedMonth, setSelectedYear, updateLocalRecord } = bookingsLogSlice.actions;

// Selectors
export const selectBookingsData = (state) => state.bookingsLog.data;
export const selectProcessedBookingsData = (state) => state.bookingsLog.processedData;
export const selectBookingsLoading = (state) => state.bookingsLog.loading;
export const selectBookingsError = (state) => state.bookingsLog.error;
export const selectSelectedMonth = (state) => state.bookingsLog.selectedMonth;
export const selectSelectedYear = (state) => state.bookingsLog.selectedYear;
export const selectCommissionSplits = (state) => state.bookingsLog.data.commissionSplits;
export const selectSalesTerritory = (state) => state.bookingsLog.data.salesTerritory;

// Selector to get commission splits for a specific project
export const selectCommissionSplitsByProject = (state, projectId) => {
  // Handle both string and number ProjectIDs
  const normalizedProjectId = normalizeIdValue(projectId);
  const allSplits = state.bookingsLog.data.commissionSplits;
  const filteredSplits = allSplits.filter(split => 
    idsMatch(getRecordId(split), normalizedProjectId)
  );
  
  return filteredSplits;
};

// Selector to calculate remaining commission balance for a project
export const selectRemainingCommissionBalance = (state, projectId) => {
  // Handle both string and number ProjectIDs
  const normalizedProjectId = normalizeIdValue(projectId);
  const existingSplits = state.bookingsLog.data.commissionSplits.filter(split => 
    idsMatch(getRecordId(split), normalizedProjectId)
  );
  const processedData = state.bookingsLog.processedData;
  const projectData = processedData.find(item => idsMatch(item.ProjectID, normalizedProjectId));
  
  if (!projectData || !projectData.InvoicePrice) {
    return { remainingPercentage: 100, remainingAmount: 0, invoicePrice: 0 };
  }
  
  const invoicePrice = parseFloat(projectData.InvoicePrice) || 0;
  const totalPercentage = existingSplits.reduce((sum, split) => {
    const percentage = parseFloat(split.CommissionPercentage) || 0;
    return sum + percentage;
  }, 0);
  const totalAmount = existingSplits.reduce((sum, split) => {
    const amount = parseFloat(split.CommissionAmount) || 0;
    return sum + amount;
  }, 0);
  
  const remainingPercentage = Math.max(0, 100 - totalPercentage);
  const remainingAmount = Math.max(0, invoicePrice - totalAmount);
  
  const result = {
    remainingPercentage: remainingPercentage,
    remainingAmount: remainingAmount,
    invoicePrice: invoicePrice,
    totalPercentage: totalPercentage,
    totalAmount: totalAmount
  };
  
  return result;
};

// Filtered selector that applies month/year filtering client-side
export const selectFilteredBookingsData = (state) => {
  const allData = state.bookingsLog.processedData;
  const selectedMonth = state.bookingsLog.selectedMonth;
  const selectedYear = state.bookingsLog.selectedYear;

  const shouldExcludeJobNumber = (jobNumber) => {
    const normalizedJobNumber = String(jobNumber || '').trim().toUpperCase();
    return normalizedJobNumber.startsWith('99') || normalizedJobNumber.startsWith('MO');
  };
  
  // Get all data for the selected year, sorted by JobNumber then by date
  const yearData = allData
    .filter(item => {
      if (item.OrderDate) {
        const { year } = getDateMonthYear(item.OrderDate);
        return year === selectedYear && !shouldExcludeJobNumber(item.JobNumber);
      }
      return false;
    })
    .sort((a, b) => {
      // Helper function to check if job number starts with priority prefixes
      const isPriorityJob = (jobNumber) => {
        if (!jobNumber) return false;
        const upperJobNumber = jobNumber.toUpperCase();
        return upperJobNumber.startsWith('PLS') || upperJobNumber.startsWith('MPO') || upperJobNumber.startsWith('MO');
      };
      
      const aPriority = isPriorityJob(a.JobNumber);
      const bPriority = isPriorityJob(b.JobNumber);
      
      // If one is priority and the other isn't, prioritize the priority job
      if (aPriority && !bPriority) {
        return -1;
      }
      if (!aPriority && bPriority) {
        return 1;
      }
      
      // Both are priority or both are not priority - sort by JobNumber first
      // Handle null/empty job numbers by putting them at the bottom
      const aJobNumber = a.JobNumber || '';
      const bJobNumber = b.JobNumber || '';
      
      // If both are empty, maintain order
      if (!aJobNumber && !bJobNumber) {
        // Then sort by OrderDate
        return createSafeDate(a.OrderDate) - createSafeDate(b.OrderDate);
      }
      
      // If only a is empty, put it after b
      if (!aJobNumber) return 1;
      
      // If only b is empty, put a before b  
      if (!bJobNumber) return -1;
      
      // Both have values, sort normally
      const jobNumberComparison = aJobNumber.localeCompare(bJobNumber, undefined, { numeric: true });
      if (jobNumberComparison !== 0) {
        return jobNumberComparison;
      }
      // Then sort by OrderDate
      return createSafeDate(a.OrderDate) - createSafeDate(b.OrderDate);
    });
  
  // Calculate YTD baseline (everything before the selected month)
  let ytdBaseline = 0;
  if (selectedMonth !== 0) {
    // Calculate total for all months before the selected month
    ytdBaseline = yearData
      .filter(item => {
        if (item.OrderDate) {
          const { month } = getDateMonthYear(item.OrderDate);
          return month < selectedMonth;
        }
        return false;
      })
      .reduce((sum, item) => sum + (parseFloat(item.InvoicePrice) || 0), 0);
  }
  
  // Filter to only the selected month (or all months if selectedMonth === 0)
  const filteredData = selectedMonth === 0 
    ? yearData // Return all months when "All" is selected
    : yearData.filter(item => {
        if (item.OrderDate) {
          const { month } = getDateMonthYear(item.OrderDate);
          return month === selectedMonth;
        }
        return false;
      });
  
  // Calculate MTD and YTD totals for the filtered data
  // YTD starts with the baseline (everything before the displayed month) and accumulates
  // MTD is the running total within the displayed data
  let mtdTotal = 0;
  let runningYTD = ytdBaseline; // Start YTD with the baseline
  
  const processedData = filteredData.map((item) => {
    const invoicePrice = parseFloat(item.InvoicePrice) || 0;
    mtdTotal += invoicePrice;
    runningYTD += invoicePrice;
    
    return {
      ...item,
      MTDTotal: mtdTotal,
      YTDTotal: runningYTD
    };
  });
  
  return processedData;
};

export default bookingsLogSlice.reducer;

export const { replaceRealtimeBookings, replaceRealtimeTerritories } = bookingsLogSlice.actions;
